import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../components/public/Seo";
import {
  BAG_CHANGED_EVENT,
  groupBagItemsByMerchant,
  removeFromBag,
  clearBag,
  getBagCount,
  buildMerchantCartUrl,
} from "../../state/bagStore";

export default function BagPage() {
  const [tick, setTick] = useState(0);
  const [openedGroups, setOpenedGroups] = useState({});

  useEffect(() => {
    const onChanged = () => setTick((t) => t + 1);
    window.addEventListener(BAG_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BAG_CHANGED_EVENT, onChanged);
  }, []);

  const groups = useMemo(() => groupBagItemsByMerchant(), [tick]);
  const bagCount = useMemo(() => getBagCount(), [tick]);

  const openedCount = Object.values(openedGroups).filter(Boolean).length;

  function openMerchant(group) {
    const directUrl = buildMerchantCartUrl(group);

    if (directUrl) {
      window.open(directUrl, "_blank", "noopener,noreferrer");
      setOpenedGroups((prev) => ({ ...prev, [group.merchantName]: true }));
      return;
    }

    const firstProductUrl =
      group.items.find((item) => item.productUrl)?.productUrl || null;

    if (firstProductUrl) {
      window.open(firstProductUrl, "_blank", "noopener,noreferrer");
      setOpenedGroups((prev) => ({ ...prev, [group.merchantName]: true }));
    }
  }

  return (
    <>
      <Seo
        title="Bag — Veya"
        description="Review your saved shopping bag by boutique, then continue to each retailer in a new tab."
      />

      <div className="public-breadcrumb">
        <Link to="/browse" className="public-navlink">
          Browse
        </Link>
        <span className="public-muted"> / Bag</span>
      </div>

      <h1 className="public-h1" style={{ marginBottom: 6 }}>
        Your Bag
      </h1>

      <p className="public-lede" style={{ marginTop: 0 }}>
        Your selections are grouped by boutique. You’ll complete purchase on each
        retailer’s website.
      </p>

      <div className="public-card" style={{ padding: 16, marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {bagCount} {bagCount === 1 ? "item" : "items"} in bag
            </div>
            <div className="public-muted" style={{ fontSize: 12, marginTop: 4 }}>
              {openedCount > 0
                ? `${openedCount} boutique ${openedCount === 1 ? "cart" : "carts"} opened in new tab`
                : "Open each boutique in a new tab, then return here for the rest."}
            </div>
          </div>

          {!!bagCount && (
            <button
              type="button"
              className="ghost-btn"
              onClick={clearBag}
              style={{ borderRadius: 12 }}
            >
              Clear Bag
            </button>
          )}
        </div>
      </div>

      {!groups.length ? (
        <div className="public-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Your bag is empty</div>
          <div className="public-muted" style={{ fontSize: 14, marginBottom: 14 }}>
            Add pieces from Browse, then come back here to open each boutique’s checkout.
          </div>
          <Link className="public-cta" to="/browse">
            Continue browsing
          </Link>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {groups.map((group) => {
            const hasOpened = !!openedGroups[group.merchantName];

            return (
              <section key={group.merchantName} className="public-card" style={{ padding: 18 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{group.merchantName}</div>
                    <div className="public-muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {group.items.length} {group.items.length === 1 ? "item" : "items"}
                      {group.merchantLocation ? ` • ${group.merchantLocation}` : ""}
                    </div>
                    <div className="public-muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Complete purchase on retailer site.
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="gold-btn"
                      onClick={() => openMerchant(group)}
                    >
                      {hasOpened ? "Open Again" : "Open Boutique Cart"}
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 12,
                  }}
                >
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        borderRadius: 16,
                        overflow: "hidden",
                        background: "rgba(255,255,255,0.34)",
                        border: "1px solid rgba(255,255,255,0.55)",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: 220,
                          background: "rgba(251,247,242,0.6)",
                        }}
                      >
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : null}
                      </div>

                      <div style={{ padding: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{item.name}</div>

                        <div
                          className="public-muted"
                          style={{ fontSize: 12, marginTop: 5, lineHeight: 1.5 }}
                        >
                          {item.category || "Item"}
                          {item.variant ? ` • ${item.variant}` : ""}
                        </div>

                        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                          {item.productUrl ? (
                            <a
                              href={item.productUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="public-navlink"
                              style={{
                                padding: "8px 10px",
                                borderRadius: 10,
                                background: "rgba(251,247,242,0.8)",
                                border: "1px solid rgba(43,43,43,0.08)",
                              }}
                            >
                              View Product
                            </a>
                          ) : null}

                          <button
                            type="button"
                            className="public-navlink"
                            onClick={() => removeFromBag(item.id)}
                            style={{
                              border: "1px solid rgba(43,43,43,0.08)",
                              background: "rgba(251,247,242,0.8)",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}